/**
 * useAppBadge — Phase 7.3 (Item A, 20-Jul).
 *
 * Owns the iOS home-screen icon badge (like WhatsApp shows "5" over its
 * app icon). Uses the Badging API (`navigator.setAppBadge` /
 * `clearAppBadge`), feature-detected — a silent no-op on browsers that
 * don't support it (which is fine, we already show the in-tab favicon
 * counter separately for those users).
 *
 * Two update paths:
 *   1. Cold-boot / manual refresh → GET /api/unread-count and set the badge.
 *   2. Live updates → the parent (InboxPage) already keeps a `conversations`
 *      state; we pass in the derived total and update the badge whenever
 *      that number changes.
 *
 * The Service Worker also calls `setAppBadge(payload.unread_count)` in its
 * push handler, so the badge stays fresh even when the PWA is closed.
 * When the PWA is open, this hook wins (client-side count = source of
 * truth) and stays consistent with SW updates.
 *
 * Usage:
 *   const totalUnread = conversations.reduce((s,c) => s + (c.unread_count||0), 0);
 *   useAppBadge(totalUnread);          // updates on change
 *   const { clear } = useAppBadge(totalUnread);
 *   // call clear() on logout to remove the badge from the home screen.
 */
import { useCallback, useEffect, useRef } from "react";
import { api } from "@/lib/api";

const badgingSupported = () =>
    typeof navigator !== "undefined" && typeof navigator.setAppBadge === "function";

async function setBadge(count) {
    if (!badgingSupported()) return;
    try {
        if (Number.isFinite(count) && count > 0) {
            await navigator.setAppBadge(count);
        } else if (typeof navigator.clearAppBadge === "function") {
            await navigator.clearAppBadge();
        }
    } catch (_e) {
        // Browsers can reject setAppBadge in private mode or when the
        // origin is not installed as a PWA — best-effort only.
    }
}

/**
 * @param {number|null} totalUnread — the current unread total. Pass null to
 *   trigger a server-side refresh via GET /api/unread-count (used on cold
 *   boot when the conversation list hasn't loaded yet).
 */
export function useAppBadge(totalUnread) {
    const lastAppliedRef = useRef(null);

    // Bootstrap: on mount, if no in-memory value yet, ask the server.
    useEffect(() => {
        let cancelled = false;
        if (totalUnread == null && badgingSupported()) {
            (async () => {
                try {
                    const { data } = await api.get("/unread-count");
                    if (!cancelled) {
                        await setBadge(data.unread_count);
                        lastAppliedRef.current = data.unread_count;
                    }
                } catch (_e) { /* silent — badge is best-effort */ }
            })();
        }
        return () => { cancelled = true; };
    // Intentionally only runs on mount + when moving from null → number.
    }, []);

    // Live updates: whenever the derived total changes, push to the OS.
    useEffect(() => {
        if (totalUnread == null) return;
        if (lastAppliedRef.current === totalUnread) return;
        lastAppliedRef.current = totalUnread;
        setBadge(totalUnread);
    }, [totalUnread]);

    const clear = useCallback(async () => {
        lastAppliedRef.current = 0;
        await setBadge(0);
    }, []);

    return { clear, supported: badgingSupported() };
}
